/**
 * =============================================================================
 * WATCH CARDS API SERVICE
 * =============================================================================
 * 
 * API service for fetching checklist items for Watch Cards.
 * Uses the /check-list-items/list endpoint to get checklist items.
 * 
 * AUTHENTICATION:
 * - JWT token from localStorage (key: "token")
 * - Merchant ID resolved via /auth/me endpoint
 * - merchant_id passed as query parameter
 * 
 * INTEGRATION (MERCHANT-AGNOSTIC):
 * - Each watch card defines alertType + generatedBy (NOT alertId)
 * - This service calls the list endpoint with generated_by filter
 * - Frontend filters by alertType and CREATED/SEEN status
 * - Response is mapped to WatchCardEvaluatedData with filters as context pills
 * 
 * NOTE: alertId is deprecated as it's merchant-specific. Use alertType instead.
 */

import type { 
    WatchCardConfig, 
    WatchCardEvaluatedData, 
    WatchCardState,
    WatchCardContextFilters,
} from '../types';
import { getMerchantId, getAuthHeaders, getApiBase } from './auth.utils';

// =============================================================================
// CONSTANTS: Valid checklist item statuses for display
// =============================================================================
const OPEN_STATUSES = ['CREATED', 'SEEN'];

// =============================================================================
// TYPE: Checklist Item Response from API
// =============================================================================
interface CheckListItemResponse {
    checklistItemId: string;
    merchantId: string;
    heading: string;
    data?: string | null;
    status: string;
    actions?: string | null;
    alertActiveFrom?: string | null;
    alertActiveUntil?: string | null;
    actionTaken?: string | null;
    actionData?: string | null;
    alertType?: string | null;
    alertLabel?: string | null;
    alertId?: string | null;
    generatedBy?: string | null;
}

// =============================================================================
// HELPER: Extract filters from checklist item data
// =============================================================================
const extractFiltersFromData = (dataJson?: string | null): WatchCardContextFilters | undefined => {
    if (!dataJson) return undefined;
    
    try {
        const data = JSON.parse(dataJson);
        
        // Try to extract filters from data.filters.a.sourceParams or data.filters.a
        if (data?.filters?.a?.sourceParams) {
            return data.filters.a.sourceParams as WatchCardContextFilters;
        }
        if (data?.filters?.a) {
            return data.filters.a as WatchCardContextFilters;
        }
        
        return undefined;
    } catch {
        return undefined;
    }
};

// =============================================================================
// EVALUATE SINGLE WATCH CARD ALERT
// =============================================================================

/**
 * Fetches checklist items for a watch card using alertType + generatedBy
 * 
 * MERCHANT-AGNOSTIC AUTHENTICATION FLOW:
 * 1. Get JWT token from localStorage
 * 2. Resolve merchant_id via /auth/me endpoint  
 * 3. Call /check-list-items/list endpoint with merchant_id and generated_by
 * 4. Filter response by alertType and CREATED/SEEN status
 * 
 * @param config - Watch card configuration with alertType, generatedBy (or legacy alertId)
 * @returns WatchCardState with heading and context filters, or null data for "Activate Story"
 */
export const evaluateWatchCardAlert = async (
    config: WatchCardConfig,
): Promise<WatchCardState> => {
    const { alertType, generatedBy, alertId, fallback } = config;
    
    try {
        // =================================================================
        // STEP 1: Resolve merchant_id from auth endpoint
        // =================================================================
        const merchantId = await getMerchantId();
        
        if (!merchantId) {
            console.error(`[WatchCardsAPI] Could not resolve merchant_id for card ${config.id}`);
            
            // Return null data - component will show "Activate Story" or auth error
            return {
                data: null,
                isLoading: false,
                error: 'Authentication required',
                lastFetched: null,
            };
        }
        
        // =================================================================
        // STEP 2: Build request with auth headers
        // =================================================================
        const headers = getAuthHeaders();
        const apiBase = getApiBase();
        
        // Build URL with merchant_id and optional generated_by filter
        let url = `${apiBase}/api/v1/check-list-items/list?merchant_id=${encodeURIComponent(merchantId)}`;
        
        // Add generated_by filter if using alertType (merchant-agnostic approach)
        if (generatedBy) {
            url += `&generated_by=${encodeURIComponent(generatedBy)}`;
        }
        
        console.log(`[WatchCardsAPI] Fetching checklist items for alertType: ${alertType || 'N/A'}, generatedBy: ${generatedBy || 'N/A'} (merchant: ${merchantId})`);
        
        // =================================================================
        // STEP 3: Call API endpoint
        // =================================================================
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const allItems: CheckListItemResponse[] = await response.json();
        
        // =================================================================
        // STEP 4: Filter for matching item with CREATED/SEEN status
        // Priority: alertType (merchant-agnostic) > alertId (legacy/merchant-specific)
        // =================================================================
        let matchingItem: CheckListItemResponse | undefined;
        
        if (alertType) {
            // NEW: Merchant-agnostic filtering by alertType
            matchingItem = allItems.find(
                (item) => item.alertType === alertType && OPEN_STATUSES.includes(item.status)
            );
            console.log(`[WatchCardsAPI] Filtering by alertType=${alertType}, found: ${matchingItem ? 'yes' : 'no'}`);
        } else if (alertId) {
            // LEGACY: Merchant-specific filtering by alertId (deprecated)
            matchingItem = allItems.find(
                (item) => item.alertId === alertId && OPEN_STATUSES.includes(item.status)
            );
            console.log(`[WatchCardsAPI] Filtering by alertId=${alertId} (deprecated), found: ${matchingItem ? 'yes' : 'no'}`);
        }
        
        if (!matchingItem) {
            console.log(`[WatchCardsAPI] No CREATED/SEEN item found for alertType: ${alertType || alertId}`);
            
            // Return null data - component will show "Activate Story" button
            // DO NOT use fallback - that shows misleading static text
            return {
                data: null,
                isLoading: false,
                error: null, // No error - just no data available
                lastFetched: new Date().toISOString(),
            };
        }
        
        // =================================================================
        // STEP 5: Extract heading and filters from the matching item
        // =================================================================
        const filters = extractFiltersFromData(matchingItem.data);
        
        const evaluatedData: WatchCardEvaluatedData = {
            heading: matchingItem.heading,
            summary: fallback?.summary || 'Click to view details',
            alertLabel: matchingItem.alertLabel || undefined,
            checklistItemId: matchingItem.checklistItemId,
            filters,
            metadata: {
                alertType: matchingItem.alertType,
                alertLabel: matchingItem.alertLabel,
                status: matchingItem.status,
                alertActiveFrom: matchingItem.alertActiveFrom,
                alertActiveUntil: matchingItem.alertActiveUntil,
            },
        };
        
        console.log(`[WatchCardsAPI] Found checklist item: ${matchingItem.checklistItemId}, heading: ${matchingItem.heading}`);
        
        return {
            data: evaluatedData,
            isLoading: false,
            error: null,
            lastFetched: new Date().toISOString(),
        };
        
    } catch (error) {
        console.error(`[WatchCardsAPI] Error fetching checklist item for alertType ${alertType || alertId}:`, error);
        
        // Return null data on error - component can decide to show error or "Activate Story"
        return {
            data: null,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch checklist item',
            lastFetched: null,
        };
    }
};

// =============================================================================
// EVALUATE MULTIPLE WATCH CARD ALERTS
// =============================================================================

/**
 * Evaluates alerts for multiple watch cards in parallel
 * 
 * @param configs - Array of watch card configurations
 * @returns Map of card ID to WatchCardState
 */
export const evaluateWatchCardAlerts = async (
    configs: WatchCardConfig[],
): Promise<Map<string, WatchCardState>> => {
    const results = await Promise.all(
        configs.map(async (config) => {
            const state = await evaluateWatchCardAlert(config);
            return { id: config.id, state };
        }),
    );
    
    return new Map(results.map(({ id, state }) => [id, state]));
};
